// tslint:disable: max-line-length
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IGroupVisitorDimensions, DefaultEventNames, IDefaultEventDimensions } from './google-tag-manager-interfaces';
import { TagManagerDataLayerService } from './tag-manager-data-layer.service';
import { IAuthenticatedUser, UserRole  } from './user-interfaces';
/** Note, we're only using the types from Foundation, which are not a run time concept. */

/** @ignore A variable representing the global Google DataLayer array. */
declare let dataLayer: Array<any>;

/**
 * The main library service which provides multiple methods for pushing various types of
 * information to Google Tag Manager in the form of DataLayer messages.
 *
 * See the following guides for more information on Universal Analytics events.
 *
 * @see https://developers.google.com/gtagjs/reference/api
 * @see https://developers.google.com/gtagjs/reference/parameter
 * @see https://developers.google.com/analytics/devguides/collection/gtagjs/migration#track_pageviews_with_the_default_tracker
 */
@Injectable()
export class GoogleAnalyticsLibService {

    /** @ignore */
    constructor (
        private _dataLayer: TagManagerDataLayerService,
        @Inject('NGX_TAG_MANAGER_FILTER_QUERY') private _filterQuery: boolean,
        @Inject('NGX_TAG_MANAGER_FILTER_EMAIL') private _filterEmail: boolean,
        @Inject('NGX_TAG_MANAGER_FILTER_PHONE') private _filterPhone: boolean
    ) { }

    /**
   * @ignore Experimental. Push an event supporting Universal Analytics ecommerce interfaces to Google Tag Manager.
   *
   * @param event
   * @param ecommerce
   * @param [eventCallback]
   * @see https://developers.google.com/tag-manager/enhanced-ecommerce
   */
    emitEcommerceEvent (event: string, ecommerce: Object, eventCallback?: Function): void {
        const ecommerceEvent = {
            event: event,
            ecommerce: ecommerce
        };
        if (eventCallback) {
            ecommerceEvent['eventCallback'] = eventCallback;
        }
        this._dataLayer.dimensions = ecommerceEvent;
        this._dataLayer.push();
    }

    /**
   * Push a Google Universal Analytics style event to Google Tag Manager. The action name used here should be whatever name
   * will be used as the `Event name` for a new `Custom Event` trigger in Tag Manager. _Never send PII in a Google Analytics event._
   *
   * @param eventActionName Recommended event or custom event name. The name that identify the event.
   * @param eventParams A payload object containing the event details.
   * @example
   * _tagManager.emitEvent('generate_lead', { 'value': '2', 'currency': 'USD', 'transaction_id': '1234' });
   *
   * @see https://developers.google.com/gtagjs/reference/api#event
   * @see https://developers.google.com/analytics/devguides/collection/gtagjs/events
   * @see https://developers.google.com/gtagjs/reference/event
   * @see https://developers.google.com/gtagjs/reference/parameter
   */
    emitEvent (eventActionName: DefaultEventNames | string, eventParams: IDefaultEventDimensions = {}): void {
        this._dataLayer.dimensions = {
            event: eventActionName,
            ...eventParams
        };
        this._dataLayer.push();
    }

    /**
   * Push information about a Universal Analytics Content Group to Google Tag Manager as a custom `ofi.content_group` event.
   *
   * @param groupIndex The index number of the Content Group index Universal Analytics. Should be 1-5.
   * @param groupName The Universal Analytics Content Group to use.
   * @see https://support.google.com/analytics/answer/2853423?hl=en
   */
    emitGroup (groupIndex: number, groupName: string): void {
        const groupOptions = {
            event: 'ofi.content_group',
            groupIndex: groupIndex,
            groupName: groupName,
            [`content_group${groupIndex}`]: groupName
        };
        this._dataLayer.dimensions = groupOptions;
        this._dataLayer.push();
    }

    /**
   * Push a virtual page view event dimension to Google Tag Manger. Note that screen views are used for mobile devices
   * whereas page views are used for websites/webapps. Also note that _sending virtual page views Still inflates page view counts_.
   *
   * @param pageURL The address Google will record for the page view event. _This should ideally start with a slash_.
   * @param pageTitle The title Google will record for the page view event.
   * @example
   * _tagManager.emitPageView(window.location.href. 'Home');
   */
    emitPageView (pageURL: string, pageTitle?: string): void {
        const pageObject: { event: string; page_path: string; page_title?: string } = {
            event: 'ofi.page_view',
            page_path: pageURL
        };
        // Add a slash to the front for GA if the URL doesn't contain one
        if (pageURL.indexOf('/') !== 0) {
            pageObject.page_path = `/${this.removeEmail(this.removeQuery(pageURL))}`;
        }
        if (pageTitle) {
            pageObject.page_title = this.removeEmail(pageTitle);
        }
        this._dataLayer.dimensions = pageObject;
        this._dataLayer.push();
    }

    /**
   * Push a virtual screen view event dimension to Google Tag Manger as a custom `ofi.screen_view` event.
   * Note that screen views are used for mobile devices whereas page views are used for websites/webapps.
   *
   * @param viewName A string that identifies name of the screen being accessed.
   * @example
   * _tagManager.emitScreenView('edit_user');
   */
    emitScreenView (viewName: string): void {
        this._dataLayer.dimensions = {
            event: 'ofi.screen_view',
            screen_name: viewName
        };
        this._dataLayer.push();
    }

    /**
   * Push the User ID to Google Tag Manager with event information as a custom `ofi.config.user_id` event. Anything other than a numeric ID will be filtered out.
   *
   * @param userId A string that identifies the individual user **without revealing PII** (E.g. the name or email.)
   * The OrthoFi ID number is recommended.
   * @example
   * _tagManager.emitUser(123456);
   */
    emitUser (userId: string): void {
    // Attempt to ensure nobody can accidentally send anything other than a numeric ID.
        const numbersOnly = /[^a-z ]\ *([.0-9])*\d/;
        const filteredId = numbersOnly.test(userId) ? undefined : userId;

        this._dataLayer.dimensions = {
            event: 'ofi.config.user_id',
            user_id: userId
        };
        this._dataLayer.push();
    }

    /**
   * **The Visitor object is meant for Pendo. Visitor information should never be sent to Google Analytics.**
   *
   * Send a full set of user dimensions to Tag Manager as a custom `ofi.config.visitor` event. This information is required
   * for Pendo. This method also fires a filtered ID as `ofi.config.user_id` for Universal Analytics.
   *
   * @example
   * _tagManager.emitVisitor({
   *   visitor_group: { id: 0, name: 'OrthoFi Orthodontics' }
   *   visitor_details: { id: 12345, name: 'Fake Gal', role: 'Guardian', practicename: 'OrthoFi Orthodontics', email: 'fakegal@orthofi.com' }
   * })
   */
    emitVisitor (user: IGroupVisitorDimensions): void {
        this._dataLayer.dimensions = {
            event: 'ofi.config.visitor',
            ...user
        };
        this._dataLayer.push();
        // Send a filtered ID as a separate event for Analytics usage.
        if (user.visitor_details && user.visitor_details.id) {
            this.emitUser(user.visitor_details.id);
        }
    }

    /**
   * Given an authenticatedUser object--a user returned from Gatekeeper--format the user in a standard way and emit it to Tag Manager.
   * @param user The logged in user structured as a standard authenticated user object from our foundational library.
   * @param getHighestRole A callback method that, given a list of roles, returns the one with most access. This is probably the highest
   * role method provided by Foundation.
   * @param envName The name of the environment this account ID is found in. This value is appended to IDs to ensure
   * they are unique, even across environments.
   */
    emitFormattedAuthenticatedUser (
        user: IAuthenticatedUser,
        getHighestRole: (roles: Array<UserRole | string>) => UserRole | string,
        envName: string
    ) {
        envName = envName.toLowerCase();
        const visitor: IGroupVisitorDimensions = {
            visitor_details: {
                id:    user.UserId ? `${envName}-${user.UserId.toString()}` : undefined,
                email: user.UserName             || undefined,
                name:  user.Name                 || undefined,
                role:  getHighestRole(user.Roles || undefined),
                environment: envName
            },
            visitor_group: {
                name: user.Practices[0].PracticeName || undefined,
                // Only one ID allowed. Are we still going to use practiceId?
                id: user.Practices[0] ? `${envName}-${user.Practices[0].PracticeId}` : undefined,
                // Custom Metadata
                organizationId:      user.OrganizationId ? `${envName}-${user.OrganizationId}` : undefined,
                organizationName:    user.OrganizationName    || undefined,
                practices:           user.Practices           || undefined,
                practiceLocationIds: user.PracticeLocationIds || undefined,
                environment:         envName
            }
        };
        if (user.OrganizationId) {
            visitor.visitor_parent_group = {
                id:   user.OrganizationId   ? `${envName}-${user.OrganizationId}` : undefined,
                name: user.OrganizationName || undefined
            };
        }
        this.emitVisitor(visitor);
    }

    /**
   * Take a router object and on each route change push an event to Tag Manager as `ofi.router_update`
   * containing the new route path and title.
   *
   * @param router The router that will be observed for changes.
   *
   * @example _tagManager.emitRouterChanges(this.myAppRouter);
   */
    emitRouterChanges (router: Router): void {
        router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const pageObject: { event: string; route_path: string; route_title?: string } = {
                    event: 'ofi.router.update',
                    route_path: this.removeEmail(this.removeQuery(event.urlAfterRedirects)),
                    route_title: document ? this.removeEmail(document.title) : undefined
                };
                this._dataLayer.dimensions = pageObject;
                this._dataLayer.push();
            }
        });
    }

    /**
   * **Sending error information to Analytics was meant for debugging the error handler library. Personal Information should not be sent to Google Analytics.**
   *
   * A helper method that allows Tag Manager to be used as an error logger in conjunction with the error handler library,
   * pushing the error information to Google Tag Manager. An exception event is sent as `ofi.exception` and an error event as
   * `ofi.error`. Note that if you send both to analytics, it may appear as 2 errors.
   *
   * @param error
   * @param [isFatal=false]
   * @param [message=error.message || 'No message sent']
   */
    logError (error: Error | HttpErrorResponse, isFatal = false, message = error.message || 'No message sent') {
        this._dataLayer.dimensions = { event: 'ofi.exception', error_description: message, fatal: isFatal, error_event: error };
        this._dataLayer.push();
        this._dataLayer.dimensions = {
            event: 'ofi.error',
            event_action: 'error',
            event_category: 'error',
            event_label: this.removeEmail(this.removePhone(this.removeQuery(message))),
            non_interaction: true
        };
        this._dataLayer.push();
    }

    /**
   * Take a string, strip any email addresses and return the sanitized value.
   *
   * @testString The string to sanitize.
   * @redaction The string to replace any sanitized values with.
   */
    removeEmail (testString: string, redaction = 'FILTERED_EMAIL'): string {
        if (this._filterEmail === false) {
            return testString;
        }
        const containsEmail = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/g;
        return testString.replace(containsEmail, redaction);
    }

    /**
   * Take a string, strip any US style phone numbers and return the sanitized value.
   *
   * @testString The string to sanitize.
   * @redaction The string to replace any sanitized values with.
   */
    removePhone (testString: string, redaction = 'FILTERED_PHONE'): string {
        if (this._filterPhone === false) {
            return testString;
        }
        const containsPhone = /([+(\d]{1})(([\d+() -.]){5,16})([+(\d]{1})/gm;
        return testString.replace(containsPhone, redaction);
    }

    /**
   * Take a URL, strip any query string properties and return the sanitized value.
   *
   * @url The URL or partial URL to sanitize.
   * @redaction The string to replace any sanitized values with.
   */
    removeQuery (url: string, redaction = ''): string {
        if (this._filterQuery === false) {
            return url;
        }
        const containsQuery = /[\?&](\w+)=(\w+)/g;
        return url.replace(containsQuery, redaction);
    }
}
